
import React, { useEffect, useState } from 'react';
import { ArrowRight, User, Users } from 'lucide-react';
import { HeroVariantProps, getTextureClass, getOutlineStyle } from '../shared';
import { StoreImage } from '../../../ui/StoreImage';

export const HeroBanner: React.FC<HeroVariantProps> = (props) => {
    const { theme, student, className, title, text, imageUrl, mobileImageUrl, onScrollToShop, parallaxRef, customButtons, customBadge, heroTextAlignment = 'left', heroButtonColor, heroButtonTextColor } = props;
    const [mounted, setMounted] = useState(false);

    useEffect(() => { setMounted(true); }, []);

    const titleColor = props.titleColor || theme?.primary || '#1A120B';
    const textColor = props.textColor || theme?.secondary || '#57534e';
    const surfaceColor = theme?.surface || '#FFFFFF';
    
    const textureClass = getTextureClass(props.heroTitleTexture);
    
    const titleOutline = getOutlineStyle(props.heroTitleOutlineWidth, props.heroTitleOutlineColor || titleColor);
    const textOutline = getOutlineStyle(props.heroTextOutlineWidth, props.heroTextOutlineColor || textColor);

    const altText = title ? title.replace(/<[^>]*>?/gm, '') : "Kaffeförsäljning till klasskassan";

    return (
        <div className={`relative ${props.heightClass} flex flex-col bg-stone-100 overflow-hidden`}>
            {/* Top Image */}
            <div className="relative flex-1 overflow-hidden">
                <div 
                    ref={parallaxRef}
                    data-parallax
                    className="absolute inset-0 w-full h-[120%] -top-[10%] overflow-hidden"
                    style={{ transform: 'translate3d(0,0,0)', willChange: 'transform' }}
                >
                    <div className={`w-full h-full transition-transform duration-[15000ms] ease-linear ${mounted ? 'scale-105' : 'scale-100'}`}>
                        <StoreImage src={imageUrl} mobileSrc={mobileImageUrl || undefined} width={1600} alt={altText} className="w-full h-full object-cover" />
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div 
                className="relative z-20 w-full px-6 py-8 border-t border-stone-100 shadow-[0_-10px_40px_rgba(0,0,0,0.03)]"
                style={{ backgroundColor: surfaceColor }}
            >
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
                    {/* Text Container */}
                    <div className="w-full md:w-2/3 relative h-auto">
                         <div 
                            className={`flex flex-col ${heroTextAlignment === 'center' ? 'items-center text-center' : heroTextAlignment === 'right' ? 'items-end text-right' : 'items-start text-left'}`}
                         >
                            {/* Integrated Seller Info (Replaces Floating Badge) */}
                            {student && (
                                <div className={`flex flex-wrap items-center gap-3 mb-4 animate-fade-in ${heroTextAlignment === 'center' ? 'justify-center' : heroTextAlignment === 'right' ? 'justify-end' : 'justify-start'}`}>
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-coffee-50 border border-coffee-100 text-[9px] font-black uppercase tracking-widest text-coffee-700">
                                        <User size={10} strokeWidth={2.5} /> {student.name}
                                    </span>
                                    {className && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-stone-50 border border-stone-100 text-[9px] font-bold uppercase tracking-widest text-stone-500">
                                            <Users size={10} strokeWidth={2.5} /> {className}
                                        </span>
                                    )}
                                </div>
                            )}

                            <h1 
                                className={`text-2xl md:text-4xl font-heading font-bold mb-2 leading-tight tracking-tight ${textureClass} ${props.titleShadowClass}`}
                                style={{ 
                                    color: textureClass ? 'transparent' : titleColor, 
                                    fontFamily: theme?.heroTitleFont,
                                    ...titleOutline
                                }}
                            >
                                {title}
                            </h1>
                            <p 
                                className={`text-xs md:text-base font-body opacity-80 leading-relaxed max-w-xl ${props.textShadowClass}`}
                                style={{ 
                                    color: textColor, 
                                    fontFamily: theme?.heroTextFont,
                                    ...textOutline
                                }}
                            >
                                {text}
                            </p>
                         </div>
                    </div>
                    
                    <div className="flex-shrink-0 w-full md:w-auto">
                        {customButtons}
                    </div>
                </div>
            </div>
        </div>
    );
};
